/**
 * 라이더 일차감 관련 유틸리티 함수
 */
import { RiderDeduction, Rider } from '../types/riderDeduction.types';
import {
    isContractEnded,
    isApplied as isAppliedGeneric,
    isFieldDisabledGeneric,
    validateNewDeductionBase
} from '@/shared/utils/deductionUtils';

// 공통 함수 재export
export { isAppliedGeneric as isApplied };

/**
 * 라이더 일차감 계약 종료 여부 확인
 * @param deduction 일차감 정보
 * @returns 계약 종료 여부
 */
export const isRiderContractTerminated = (deduction: RiderDeduction): boolean => {
    return isContractEnded(deduction.contEdDate);
};

/**
 * 라이더 계약 종료 여부 확인
 * @param rider 라이더 정보
 * @returns 계약 종료 여부
 */
export const isRiderTerminated = (rider: Rider): boolean => {
    return isContractEnded(rider.contEdDate);
};

/**
 * 수정/삭제 불가 여부 (계약 종료된 항목만 완전히 비활성화)
 * @param deduction 일차감 정보
 * @returns 비활성화 여부
 */
export const isDisabled = (deduction: RiderDeduction): boolean => {
    return isRiderContractTerminated(deduction);
};

/**
 * 특정 필드 수정 불가 여부 (반영된 항목은 종료일자 외 수정 불가)
 * @param deduction 일차감 정보
 * @param field 필드명
 * @param today 오늘 날짜 (YYYY-MM-DD)
 * @returns 필드 수정 불가 여부
 */
export const isFieldDisabled = (deduction: RiderDeduction, field: string, today: string): boolean => {
    if (isRiderContractTerminated(deduction)) return true;
    return isFieldDisabledGeneric(deduction, field, today);
};

/**
 * 계약 중인 라이더만 필터링
 * @param riders 전체 라이더 목록
 * @returns 계약 중인 라이더 목록
 */
export const filterActiveRiders = (riders: Rider[]): Rider[] => {
    return riders.filter(rider => !isRiderTerminated(rider));
};

/**
 * 새 일차감 입력값 유효성 검사
 * @param newDeduction 새 일차감 데이터
 * @returns 유효성 검사 결과
 */
export const validateRiderNewDeduction = (newDeduction: {
    riderId: string;
    deductionTypeCode: string;
    deductionName: string;
    deductionAmount: string;
    startDate: string;
    endDate: string;
}): boolean => {
    return !!(newDeduction.riderId && validateNewDeductionBase(newDeduction));
};

// ===== 날짜 유틸리티 (DeductionRiderToHeadPage 전용) =====

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** "20260601" → "2026-06-01" 자동 변환 (8자리 숫자일 때만) */
export const normalizeDate = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    return value;
};

/** 시작일 + 일수 → 종료일 계산 (시작일 포함, 1일 = 당일만) */
export const calcEndDate = (startDate: string, days: number): string => {
    if (!startDate || days <= 0 || !DATE_RE.test(startDate)) return '';
    const d = new Date(startDate);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days - 1);
    return d.toISOString().split('T')[0];
};

/** 시작일 + 종료일 → 일수 계산 (양 끝 포함) */
export const calcDays = (startDate: string, endDate: string): number => {
    if (!DATE_RE.test(startDate) || !DATE_RE.test(endDate)) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};
